// What changed between a contract (or an earlier draft) and a newer version of its document that
// arrived later in the same email thread, and applying those changes. Only what the new version
// states counts: a detail it doesn't mention isn't a change, so nothing is ever cleared.
import { fieldKind, fieldLabel, typeFor } from "./catalog";
import type { ContractFields } from "./contract";
import { formatDate } from "./dates";

export interface FieldChange {
  /** A ContractFields key, "details.<term>" for a term, or "parties" for people added. */
  field: string;
  label: string;
  from: string | null;
  to: string;
}

const RENEWAL = { none: "Ends on the end date", auto: "Renews automatically", manual: "Renewable if you choose" } as const;

function detailText(key: string, value: string, currency: string | null): string {
  if (fieldKind(key) === "date") return formatDate(value, "en-IN");
  if (fieldKind(key) === "money") {
    const n = Number(value);
    if (!Number.isFinite(n)) return value;
    try {
      return currency ? new Intl.NumberFormat("en-IN", { style: "currency", currency, minimumFractionDigits: Number.isInteger(n) ? 0 : 2, maximumFractionDigits: 2 }).format(n) : n.toLocaleString("en-IN");
    } catch {
      return `${n} ${currency ?? ""}`.trim();
    }
  }
  return value;
}

export function contractChanges(before: ContractFields, after: ContractFields): FieldChange[] {
  const out: FieldChange[] = [];
  const date = (d: string | null) => (d ? formatDate(d, "en-IN") : null);
  if (after.type && after.type !== before.type) out.push({ field: "type", label: "Type", from: typeFor(before.type)?.label ?? null, to: typeFor(after.type)?.label ?? after.type });
  if (after.startDate && after.startDate !== before.startDate) out.push({ field: "startDate", label: "Start date", from: date(before.startDate), to: date(after.startDate)! });
  if (after.endDate && after.endDate !== before.endDate) out.push({ field: "endDate", label: "End date", from: date(before.endDate), to: date(after.endDate)! });
  if (after.noticePeriodDays !== null && after.noticePeriodDays !== before.noticePeriodDays) {
    out.push({ field: "noticePeriodDays", label: "Notice period", from: before.noticePeriodDays === null ? null : `${before.noticePeriodDays} days`, to: `${after.noticePeriodDays} days` });
  }
  if (after.renewalType !== "none" && after.renewalType !== before.renewalType) out.push({ field: "renewalType", label: "Renewal", from: RENEWAL[before.renewalType], to: RENEWAL[after.renewalType] });
  if (after.renewalTermMonths !== null && after.renewalTermMonths !== before.renewalTermMonths) {
    out.push({ field: "renewalTermMonths", label: "Renewal term", from: before.renewalTermMonths === null ? null : `${before.renewalTermMonths} months`, to: `${after.renewalTermMonths} months` });
  }
  const currency = before.currency ?? after.currency;
  for (const [key, value] of Object.entries(after.details)) {
    if (!value || value === before.details[key]) continue;
    out.push({ field: `details.${key}`, label: fieldLabel(key), from: before.details[key] ? detailText(key, before.details[key]!, currency) : null, to: detailText(key, value, currency) });
  }
  const known = new Set(before.parties.map((p) => p.name.toLowerCase()));
  const added = after.parties.filter((p) => !known.has(p.name.toLowerCase()));
  if (added.length) out.push({ field: "parties", label: "People added", from: null, to: added.map((p) => (p.role ? `${p.name} (${p.role})` : p.name)).join(", ") });
  return out;
}

/** `before` with the changes `after` brings (see contractChanges). */
export function applyChanges(before: ContractFields, after: ContractFields): ContractFields {
  const next: ContractFields = { ...before, details: { ...before.details }, parties: [...before.parties] };
  for (const c of contractChanges(before, after)) {
    if (c.field.startsWith("details.")) {
      const key = c.field.slice("details.".length);
      next.details[key] = after.details[key]!;
    } else if (c.field === "parties") {
      const known = new Set(before.parties.map((p) => p.name.toLowerCase()));
      next.parties.push(...after.parties.filter((p) => !known.has(p.name.toLowerCase())));
    } else {
      (next as unknown as Record<string, unknown>)[c.field] = after[c.field as keyof ContractFields];
    }
  }
  if (!next.currency && after.currency) next.currency = after.currency;
  return next;
}

/** "Rent: ₹25,000 → ₹27,000" or "Notice period: 60 days (new)". */
export const changeLine = (c: FieldChange) => (c.from ? `${c.label}: ${c.from} → ${c.to}` : `${c.label}: ${c.to} (new)`);
